import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';

import { type TableStateControl, useTableSettings, useTableStoreState } from '../../hooks';
import { createEmptyFilterGroup } from '../../hooks/useTableStateControl.utils';
import { applyClientTableState } from '../../utils/clientTableState';
import type { BulkActionOverlayAction } from '../BulkActionOverlay';
import {
  type ServerSideSelectionState,
  type TableBaseProps,
  type TableExportParams,
  TableShell,
} from '../TableShell/TableShell';

export type ClientTableProps<TData> = TableBaseProps<TData> & {
  /** Every row; the table filters, searches, sorts and pages them in the browser. */
  data: TData[];
  /** Table state; defaults to component state, with filters, search and sort kept for the session under `id`. */
  stateControl?: TableStateControl;
  /**
   * Called with the selected rows; "select all" resolves to every row matching the current filters.
   * Rows are matched by `getRowId`, or by their `id`, `uuid` or position in `data` when it's omitted.
   */
  onSelectionChange?: (rows: TData[]) => void;
  /** Actions shown while rows are selected; each receives the selected rows. */
  bulkActions?: Array<Omit<BulkActionOverlayAction, 'onClick'> & { onClick: (rows: TData[]) => void }>;
  /** "Export Visible": `data` is every top-level row matching the current filters and search, in the current sort order. */
  onExportVisible?: (params: TableExportParams<TData>) => void;
  /** "Export All": `data` is every top-level row, ignoring filters and search, in the current sort order. */
  onExportAll?: (params: TableExportParams<TData>) => void;
  /** Page the rows. Defaults to true; when false every matching row is shown. */
  enablePagination?: boolean;
};

/**
 * ClientTable holds all of its data: pass every row and it searches, filters, sorts and pages them in the browser.
 * For data the API filters and pages, use {@link ServerTable}. Both share the same `Table` grid, toolbar, settings menu,
 * filter dialog, selection and export.
 *
 * Controls are on by default; turn off any a table doesn't need with `enableSearch`, `enableFilters`, `enableSettings`,
 * `enableHiding`, `enableColumnPinning`, `enableColumnOrdering` and `enablePagination`.
 * Row checkboxes show when the page passes `onSelectionChange` or `bulkActions`; `enableRowSelection` overrides that.
 *
 * Filters and search read each column's value; give object-valued columns a `meta.filterValueConfig.accessor`
 * (e.g. `name`) so they match on text, a `meta.sortKey` to sort by something other than the value, or a
 * `meta.sortCompare` to sort with a comparator. With `getSubRows`, filters, search and sorting apply to top-level
 * rows, like ServerTable: each match keeps all its children, in the order given.
 *
 * @example
 * ```tsx
 * <ClientTable id="utility-contacts" columns={columns} data={contacts} isLoading={isLoading} />
 * ```
 */
function ClientTable<TData>({
  id,
  columns,
  data,
  stateControl: stateControlProp,
  isLoading,
  getRowId,
  onSelectionChange,
  bulkActions,
  onExportVisible,
  onExportAll,
  enablePagination = true,
  enableFilters = true,
  enableSearch = true,
  enableRowSelection: enableRowSelectionProp,
  selectionResetKey,
  getSubRows,
  ...props
}: ClientTableProps<TData>) {
  // Starts on the page size the settings menu saved under the same id; "Reset view" still returns to the default.
  const settings = useTableSettings({ id });
  const { initialPageSize } = settings;
  const ownStateControl = useTableStoreState({
    storageKey: id,
    initialPagination: initialPageSize ? { pageIndex: 0, pageSize: initialPageSize } : undefined,
  });
  const noFilters = useMemo(() => createEmptyFilterGroup(), []);
  // Saved filters and search apply only while their controls show; a page's own `stateControl` applies as passed.
  const stateControl = stateControlProp ?? {
    ...ownStateControl,
    filters: enableFilters ? ownStateControl.filters : noFilters,
    globalFilter: enableSearch ? ownStateControl.globalFilter : '',
  };
  const { filters, sorting, globalFilter, pagination, setPagination } = stateControl;

  // Like ServerTable, filters, search and sorting apply to top-level rows; children keep the order given.
  const matching = useMemo(
    () => applyClientTableState(data, columns, { filters, sorting, globalFilter }),
    [data, columns, filters, sorting, globalFilter],
  );

  const page = useMemo(
    () =>
      enablePagination
        ? matching.slice(pagination.pageIndex * pagination.pageSize, (pagination.pageIndex + 1) * pagination.pageSize)
        : matching,
    [enablePagination, matching, pagination.pageIndex, pagination.pageSize],
  );

  // Go back to the first page when filters or new data leave the current page empty.
  useEffect(() => {
    if (pagination.pageIndex > 0 && pagination.pageIndex * pagination.pageSize >= matching.length) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [matching.length, pagination.pageIndex, pagination.pageSize, setPagination]);

  // Every row in `data`, children included; rows without an `id` or `uuid` are named by their position in the tree.
  const { allRows, positions } = useMemo(() => {
    const rows: TData[] = [];
    const paths = new Map<TData, string>();
    const walk = (level: TData[], parent: string | null) =>
      level.forEach((row, index) => {
        const path = parent === null ? String(index) : `${parent}.${index}`;
        rows.push(row);
        paths.set(row, path);
        const children = getSubRows?.(row);
        if (children) walk(children, path);
      });
    walk(data, null);
    return { allRows: rows, positions: paths };
  }, [data, getSubRows]);
  const rowId = useMemo(
    () =>
      getRowId ??
      ((row: TData) => {
        const record = row as Record<string, unknown>;
        return String(record.id ?? record.uuid ?? `row-${positions.get(row)}`);
      }),
    [getRowId, positions],
  );
  const selectedRows = useCallback(
    (selection: ServerSideSelectionState) => {
      // Like ServerTable, "select all" covers the matching top-level rows; child rows are selected one by one.
      if (selection.mode === 'all') {
        const excluded = new Set(selection.excludedIds);
        return matching.filter((row) => !excluded.has(rowId(row)));
      }
      const selected = new Set(selection.selectedIds);
      return allRows.filter((row) => selected.has(rowId(row)));
    },
    [matching, allRows, rowId],
  );

  const enableRowSelection =
    enableRowSelectionProp ?? (onSelectionChange !== undefined || (bulkActions?.length ?? 0) > 0);

  // A new search or filter clears the selection, so the selected count always matches the rows a bulk action gets.
  const queryResetKey = useMemo(
    () => JSON.stringify([selectionResetKey ?? null, filters, globalFilter ?? '']),
    [selectionResetKey, filters, globalFilter],
  );

  // The same selection covers different rows once the data changes.
  const [selection, setSelection] = useState<ServerSideSelectionState | null>(null);
  const selected = useMemo(() => (selection ? selectedRows(selection) : null), [selection, selectedRows]);

  const lastNotified = useRef<TData[] | null>(null);
  const notifySelection = useEffectEvent((rows: TData[]) => {
    const previous = lastNotified.current;
    if (previous && previous.length === rows.length && previous.every((row, index) => row === rows[index])) return;
    lastNotified.current = rows;
    onSelectionChange?.(rows);
  });
  useEffect(() => {
    if (selected) notifySelection(selected);
  }, [selected]);

  return (
    <TableShell
      {...props}
      id={id}
      columns={columns}
      data={page}
      rowCount={matching.length}
      settings={settings}
      stateControl={stateControl}
      isLoading={isLoading}
      getRowId={rowId}
      getSubRows={getSubRows}
      onExportVisible={onExportVisible ? (params) => onExportVisible({ ...params, data: matching }) : undefined}
      onExportAll={
        onExportAll
          ? (params) =>
              onExportAll({
                ...params,
                data: applyClientTableState(data, columns, { filters: createEmptyFilterGroup(), sorting }),
              })
          : undefined
      }
      enablePagination={enablePagination}
      enableFilters={enableFilters}
      enableSearch={enableSearch}
      enableRowSelection={enableRowSelection}
      selectionResetKey={queryResetKey}
      onSelectionChange={onSelectionChange ? setSelection : undefined}
      bulkActions={bulkActions?.map((action) => ({
        ...action,
        onClick: (selection) => action.onClick(selectedRows(selection)),
      }))}
    />
  );
}

export { ClientTable };
